import { useEffect, useState } from 'react';

// `window.OneTrust` is assigned by OneTrust's script before all of its methods
// are attached, so a bare `!!window.OneTrust` is truthy while `OnConsentChanged`
// is still undefined. Gate on the method itself so "ready" means fully wired.
const isOneTrustFullyLoaded = (): boolean =>
  typeof window.OneTrust?.OnConsentChanged === 'function';

/**
 * Resolves to `true` once the OneTrust SDK has fully initialized.
 *
 * OneTrust exposes no ready event; it invokes `window.OptanonWrapper` once
 * loaded. This patches that global (chaining any existing wrapper) to flip the
 * flag, then restores it on cleanup. No-op while `isEnabled` is false.
 *
 * @param isEnabled - when false, the hook stays idle and patches nothing
 */
export const useIsOneTrustInitialized = (isEnabled = true): boolean => {
  const [isReady, setIsReady] = useState(isOneTrustFullyLoaded);

  useEffect(() => {
    const originalOptanonWrapper = window.OptanonWrapper;

    if (isEnabled && !isReady) {
      window.OptanonWrapper = () => {
        originalOptanonWrapper?.();
        setIsReady(true);
      };
    }

    return () => {
      window.OptanonWrapper = originalOptanonWrapper;
    };
  }, [isEnabled, isReady]);

  return isReady;
};
